import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'General Inquiry',
    message: ''
  });

  const contactMethods = [
    {
      icon: 'Mail',
      title: 'Email Support',
      description: 'enterprise@firstondate.com',
      subtitle: 'Response within 4 business hours',
      action: () => window.open('mailto:enterprise@firstondate.com', '_blank'),
      color: 'text-primary'
    },
    {
      icon: 'MessageCircle',
      title: 'Live Chat',
      description: '24/7 for Enterprise customers',
      subtitle: 'Average response time: 2 minutes',
      action: () => console.log('Open chat widget'),
      color: 'text-success'
    },
    {
      icon: 'Phone',
      title: 'Phone Support',
      description: '+1 (646) 558-0123 (US)',
      subtitle: '+44 20 7946 0958 (UK)',
      action: () => window.open('tel:+16465580123', '_blank'),
      color: 'text-accent'
    },
    {
      icon: 'Calendar',
      title: 'Book a Demo',
      description: 'Schedule with sales team',
      subtitle: '30-minute platform walkthrough',
      action: () => window.open('/book-demo', '_blank'),
      color: 'text-warning'
    },
  ];

  const faqTopics = [
    {
      question: 'What is your SLA for enterprise support?',
      answer: 'Enterprise customers receive <4 hour response time for P1 issues, 24/7 dedicated support channel, and quarterly business reviews with a Customer Success Manager.'
    },
    {
      question: 'Do you offer on-premise deployment?',
      answer: 'Yes, we offer private cloud and on-premise deployment options for enterprise customers with strict data residency requirements. Contact sales for custom deployment architecture.'
    },
    {
      question: 'What integrations are available?',
      answer: 'We integrate with 30+ platforms including Slack, Teams, Drive, SharePoint, Workday, BambooHR, Jira, and more. Custom integrations available via REST API and webhooks.'
    },
    {
      question: 'How does pricing work for large teams?',
      answer: 'Enterprise pricing is volume-based with custom quotes for 500+ users. Includes unlimited seats for admins, dedicated CSM, advanced security features, and priority support.'
    },
  ];

  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission logic
  };

  return (
    <section id="contact" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center">
            <Icon name="MessageCircle" size={32} className="text-success" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">Contact Support</h2>
            <p className="text-muted-foreground mt-1">24/7 enterprise assistance across all channels</p>
          </div>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {contactMethods?.map((method, index) => (
            <button
              key={index}
              onClick={method?.action}
              className="bg-background/50 border border-border rounded-lg p-6 hover:border-success/50 hover:shadow-glow transition-all text-left group"
            >
              <div className={`w-12 h-12 ${method?.color?.replace('text', 'bg')}/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <Icon name={method?.icon} size={24} className={method?.color} />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{method?.title}</h3>
              <p className="text-sm text-muted-foreground mb-1">{method?.description}</p>
              <p className="text-xs text-muted-foreground">{method?.subtitle}</p>
            </button>
          ))}
        </div>

        {/* Contact Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div>
            <h3 className="text-xl font-bold text-foreground mb-4">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Full Name"
                placeholder="John Doe"
                value={formData?.name}
                onChange={(e) => setFormData({...formData, name: e?.target?.value})}
                required
              />
              <Input
                label="Work Email"
                type="email"
                placeholder="john@company.com"
                value={formData?.email}
                onChange={(e) => setFormData({...formData, email: e?.target?.value})}
                required
              />
              <Input
                label="Company Name"
                placeholder="Acme Corp"
                value={formData?.company}
                onChange={(e) => setFormData({...formData, company: e?.target?.value})}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Subject</label>
                <select
                  value={formData?.subject}
                  onChange={(e) => setFormData({...formData, subject: e?.target?.value})}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>General Inquiry</option>
                  <option>Technical Support</option>
                  <option>Sales Question</option>
                  <option>Partnership Opportunity</option>
                  <option>Feature Request</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Message</label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => setFormData({...formData, message: e?.target?.value})}
                  placeholder="Tell us how we can help..."
                  rows={5}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  required
                />
              </div>
              <Button 
                type="submit"
                variant="default"
                iconName="Send"
                iconPosition="right"
                className="w-full"
              >
                Send Message
              </Button>
            </form>
          </div>

          {/* FAQ Section */}
          <div>
            <h3 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {faqTopics?.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-background/50 border border-border rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-card/30 transition-colors"
                  >
                    <span className="font-medium text-foreground pr-4">{faq?.question}</span>
                    <Icon 
                      name={expandedFAQ === index ? 'ChevronUp' : 'ChevronDown'} 
                      size={20} 
                      className="text-muted-foreground flex-shrink-0"
                    />
                  </button>
                  {expandedFAQ === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Button 
              variant="outline"
              iconName="HelpCircle"
              iconPosition="left"
              className="w-full mt-4"
              onClick={() => window.open('/help-center', '_blank')}
            >
              Visit Full Help Center
            </Button>
          </div>
        </div>

        {/* Support Commitment */}
        <div className="p-6 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-start gap-4">
            <Icon name="Clock" size={24} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Enterprise Support Commitment</h3>
              <p className="text-sm text-muted-foreground mb-3">
                All enterprise customers receive dedicated support with guaranteed response times: P0 incidents (15 min), P1 (4 hours), P2 (24 hours), P3 (48 hours). Our global support team operates 24/7 across US, EU, and APAC time zones.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-success/20 text-success text-xs font-semibold rounded-full">99.8% CSAT Score</span>
                <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-semibold rounded-full">Avg Response: 2.3 hours</span>
                <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-semibold rounded-full">24/7 Availability</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;